/**
 * ErrorBoundary Component
 * REQ-CLIENT-002: ISSUE-007 — Catch uncaught React errors and show fallback UI.
 *
 * Prevents a single component crash from white-screening the entire app.
 * Logs the error to console.error and provides a "Try Again" reset mechanism.
 *
 * @apal/shared-ui
 */

import * as React from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ErrorBoundaryProps {
  /** The component tree to protect */
  children: React.ReactNode;
  /** Optional custom fallback UI. Receives error and reset function. */
  fallback?: React.ReactNode | ((props: { error: Error; resetErrorBoundary: () => void }) => React.ReactNode);
  /** Optional callback when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Optional label for the section being protected (used in fallback UI) */
  sectionName?: string;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * React class component that implements the Error Boundary pattern.
 *
 * Class components are required because React does not yet support
 * getDerivedStateFromError / componentDidCatch in function components.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      // Custom fallback provided
      if (this.props.fallback) {
        if (typeof this.props.fallback === "function") {
          return this.props.fallback({
            error: this.state.error,
            resetErrorBoundary: this.resetErrorBoundary,
          });
        }
        return this.props.fallback;
      }

      // Default fallback UI
      const sectionLabel = this.props.sectionName
        ? `in ${this.props.sectionName}`
        : "in this section";

      return (
        <div
          data-testid="error-boundary-fallback"
          style={{
            padding: "24px",
            margin: "16px 0",
            borderRadius: "8px",
            backgroundColor: "#FFF3F0",
            border: "1px solid #FFCCC7",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#CF1322",
              marginBottom: "8px",
            }}
          >
            Something went wrong
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#595959",
              marginBottom: "16px",
            }}
          >
            An unexpected error occurred {sectionLabel}. Please try again.
          </div>
          <button
            data-testid="error-boundary-retry-btn"
            onClick={this.resetErrorBoundary}
            style={{
              padding: "8px 24px",
              fontSize: "14px",
              fontWeight: 500,
              color: "#FFFFFF",
              backgroundColor: "#1890FF",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
